class DealerCarsFilter extends HTMLElement {
    constructor() {
      super();
      this.isInitialized = false;
      this.carsPerPage = parseInt(this.dataset.carsPerPage) || 12;
      this.currentPage = 1;
      this.filteredCars = [];
      this.isDrawerOpen = false;
      this.updateHeaderHeight = this.updateHeaderHeight.bind(this);
    }
  
    connectedCallback() {
      if (this.isInitialized) return;
      
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init());
      } else {
        this.init();
      }
      
      // Add resize observer for header height updates
      this.setupHeaderHeightObserver();
      
      this.isInitialized = true;
    }
  
    async init() {
      this.filterForm = this.querySelector('form');
      this.resultsContainer = document.querySelector('.dealer-cars-grid');
  
      if (!this.filterForm || !this.resultsContainer) {
        console.error('Required elements not found. Make sure form and results container exist.');
        return;
      }
  
      this.bindEvents();
      await this.loadInitialCars();
    }
  
    async loadInitialCars() {
      try {
        const response = await this.fetchCars();
        this.populateFilterOptions(response);
        this.renderResults(response);
      } catch (error) {
        console.error('Error loading initial cars:', error);
        this.resultsContainer.innerHTML = '<div class="error">Error loading cars. Please try again later.</div>';
      }
    }
  
    async fetchCars() {
      const query = `
        query GetDealerCars($first: Int!, $sortKey: String, $reverse: Boolean) {
          metaobjects(
            type: "dealer_cars"
            first: $first
            sortKey: $sortKey
            reverse: $reverse
          ) {
            edges {
              node {
                handle
                fields {
                  key
                  value
                  reference {
                    __typename
                    ... on MediaImage {
                      image {
                        url(transform: {preferredContentType: WEBP, maxWidth: 800})
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;
  
      try {
        this.resultsContainer.innerHTML = '<div class="loading">Loading cars...</div>';
  
        const response = await fetch('/api/graphql', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': window.dealerCarsConfig.storefrontAccessToken
          },
          body: JSON.stringify({ 
            query,
            variables: {
              first: 250, // Increased to get more cars since we're filtering client-side
              sortKey: "updated_at",
              reverse: true
            }
          })
        });
  
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
  
        return await response.json();
      } catch (error) {
        console.error('Error fetching data:', error);
        throw error;
      }
    }
  
    bindEvents() {
      // Add mobile drawer events
      const openButton = this.querySelector('[data-action="open-filters"]');
      const closeButton = this.querySelector('[data-action="close-filters"]');
      const overlay = this.querySelector('[data-filters-overlay]');
      const sidebar = this.querySelector('.filters-sidebar');

      if (openButton) {
        openButton.addEventListener('click', () => this.toggleDrawer(true));
      }

      if (closeButton) {
        closeButton.addEventListener('click', () => this.toggleDrawer(false));
      }

      if (overlay) {
        overlay.addEventListener('click', () => this.toggleDrawer(false));
      }

      // Close drawer when form is submitted on mobile
      this.filterForm.addEventListener('submit', (event) => {
        event.preventDefault();
        this.currentPage = 1;
        this.onSubmitHandler(event);
        if (window.innerWidth < 768) {
          this.toggleDrawer(false);
        }
      });

      // Handle form submit for filtering
      this.filterForm.addEventListener('submit', (event) => {
        event.preventDefault();
        this.currentPage = 1; // Reset to first page when filtering
        this.onSubmitHandler(event);
      });

      // Handle clear filters
      const clearButton = this.filterForm.querySelector('.button--secondary');
      if (clearButton) {
        clearButton.addEventListener('click', (e) => {
          e.preventDefault();
          this.filterForm.reset();
          this.currentPage = 1; // Reset to first page when clearing
          this.loadInitialCars();
        });
      }
    }
  
    async onSubmitHandler(event) {
      event.preventDefault();
      const formData = new FormData(this.filterForm);
      
      try {
        const response = await this.fetchCars();
        this.renderResults(response, formData);
      } catch (error) {
        console.error('Error fetching results:', error);
      }
    }
  
    renderResults(response, formData = new FormData()) {
      if (!response.data?.metaobjects?.edges) {
        console.error('Invalid response format:', response);
        this.resultsContainer.innerHTML = '<div class="error">Error loading cars. Please try again later.</div>';
        return;
      }

      // Get filter values
      const year = formData.get('year');
      const model = formData.get('model');
      const trim = formData.get('trim');
      const rtrPackage = formData.get('rtr_package');
      const engine = formData.get('engine');
      const colour = formData.get('colour');
      const dealer = formData.get('dealer');

      // Filter cars based on selected criteria
      this.filteredCars = response.data.metaobjects.edges.filter(({ node }) => {
        const fields = {};
        node.fields.forEach(field => {
          fields[field.key] = field.value;
        });

        return (!year || fields.car_year === year) &&
               (!model || fields.car_model === model) &&
               (!trim || fields.trim_level === trim) &&
               (!rtrPackage || fields.rtr_package === rtrPackage) &&
               (!engine || fields.car_engine === engine) &&
               (!colour || fields.car_colour === colour) &&
               (!dealer || fields.dealer === dealer);
      });

      this.renderPage();
    }

    renderPage() {
      const totalPages = Math.ceil(this.filteredCars.length / this.carsPerPage);
      const start = (this.currentPage - 1) * this.carsPerPage;
      const paginatedCars = this.filteredCars.slice(start, start + this.carsPerPage);

      // Update all showing cars and total cars counters
      const showingCarsElements = this.querySelectorAll('[data-showing-cars]');
      const totalCarsElements = this.querySelectorAll('[data-total-cars]');
      
      showingCarsElements.forEach(element => {
        element.textContent = paginatedCars.length;
      });
      
      totalCarsElements.forEach(element => {
        element.textContent = this.filteredCars.length;
      });

      const carsHTML = paginatedCars.map(({ node }, index) => {
        const fields = {};
        
        node.fields.forEach(field => {
          if (field.key === 'car_image') {
            fields[field.key] = field.reference?.image?.url || null;
          } else {
            fields[field.key] = field.value;
          }
        });

        return `
          <div class="dealer-car-card" style="--animation-delay: ${index}">
            ${fields.car_image ? `
              <a href="${fields.dealer_page}" target="_blank">
                <img
                  src="${fields.car_image}"
                  alt="${fields.car_model || ''} ${fields.car_year || ''}"
                  class="dealer-car-image"
                  loading="lazy"
                >
              </a>
            ` : ''}
            <h3>${fields.car_year || ''} ${fields.car_model || ''} ${fields.rtr_package || ''}</h3>
            <p><span class="title">Trim:</span> ${fields.trim_level || ''}</p>
            <p><span class="title">RTR Package:</span> ${fields.rtr_package || ''}</p>
            <p><span class="title">Engine:</span> ${fields.car_engine || ''}</p>
            <p><span class="title">Color:</span> ${fields.car_colour || ''}</p>
            <p><span class="title">Dealer:</span> ${fields.dealer || ''}</p>
            <p><span class="title">VIN:</span> ${fields.vin_number || ''}</p>
            ${fields.dealer_page ? `
              <a href="${fields.dealer_page}" target="_blank" class="button button--primary">View Dealer Page</a>
            ` : ''}
          </div>
        `;
      }).join('');

      // Update the cars grid
      this.resultsContainer.innerHTML = carsHTML || '<div class="text-center"><p>No cars found matching your criteria.</p></div>';

      // Update pagination
      const pagination = this.querySelector('[data-pagination-template]');
      if (pagination) {
        if (totalPages > 1) {
          pagination.style.display = 'block';
          this.querySelector('[data-current-page]').textContent = this.currentPage;
          this.querySelector('[data-total-pages]').textContent = totalPages;
          
          // Update button states
          const prevButton = this.querySelector('[data-action="previous"]');
          const nextButton = this.querySelector('[data-action="next"]');
          
          if (prevButton) {
            prevButton.disabled = this.currentPage === 1;
            if (!prevButton.hasEventListener) {
              prevButton.addEventListener('click', () => this.changePage('previous'));
              prevButton.hasEventListener = true;
            }
          }
          
          if (nextButton) {
            nextButton.disabled = this.currentPage === totalPages;
            if (!nextButton.hasEventListener) {
              nextButton.addEventListener('click', () => this.changePage('next'));
              nextButton.hasEventListener = true;
            }
          }
        } else {
          pagination.style.display = 'none';
        }
      }
    }

    changePage(direction) {
      const totalPages = Math.ceil(this.filteredCars.length / this.carsPerPage);
      
      if (direction === 'previous' && this.currentPage > 1) {
        this.currentPage--;
      } else if (direction === 'next' && this.currentPage < totalPages) {
        this.currentPage++;
      }

      // Scroll to top of results
      this.resultsContainer.scrollIntoView({ behavior: 'smooth' });
      
      this.renderPage();
    }

    toggleDrawer(open) {
      const sidebar = this.querySelector('.filters-sidebar');
      const overlay = this.querySelector('[data-filters-overlay]');
      
      if (open) {
        sidebar.classList.add('is-open');
        overlay.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      } else {
        sidebar.classList.remove('is-open');
        overlay.classList.remove('is-open');
        document.body.style.overflow = '';
      }
      
      this.isDrawerOpen = open;
    }

    setupHeaderHeightObserver() {
      // Initial header height calculation
      this.updateHeaderHeight();

      // Update on resize
      window.addEventListener('resize', this.updateHeaderHeight);

      // Update on scroll (for sticky header changes)
      window.addEventListener('scroll', this.updateHeaderHeight);

      // Observe header wrapper changes
      const headerWrapper = document.querySelector('.header__wrapper');
      if (headerWrapper) {
        const observer = new ResizeObserver(this.updateHeaderHeight);
        observer.observe(headerWrapper);
      }
    }

    updateHeaderHeight() {
      const headerWrapper = document.querySelector('.header__wrapper');
      if (headerWrapper) {
        const headerHeight = headerWrapper.offsetHeight;
        document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
        
        // If there's a different height for sticky header, update that too
        if (document.body.classList.contains('shopify-section-header-sticky')) {
          const stickyHeaderHeight = headerWrapper.offsetHeight;
          document.documentElement.style.setProperty('--header-height-sticky', `${stickyHeaderHeight}px`);
        }
      }
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.updateHeaderHeight);
      window.removeEventListener('scroll', this.updateHeaderHeight);
    }

    populateFilterOptions(response) {
      if (!response.data?.metaobjects?.edges) return;

      const filterValues = {
        years: new Set(),
        models: new Set(),
        trim_levels: new Set(),
        rtr_packages: new Set(),
        engines: new Set(),
        colours: new Set(),
        dealers: new Set()
      };

      // Collect all unique values
      response.data.metaobjects.edges.forEach(({ node }) => {
        const fields = {};
        node.fields.forEach(field => {
          fields[field.key] = field.value;
        });

        if (fields.car_year) filterValues.years.add(fields.car_year);
        if (fields.car_model) filterValues.models.add(fields.car_model);
        if (fields.trim_level) filterValues.trim_levels.add(fields.trim_level);
        if (fields.rtr_package) filterValues.rtr_packages.add(fields.rtr_package);
        if (fields.car_engine) filterValues.engines.add(fields.car_engine);
        if (fields.car_colour) filterValues.colours.add(fields.car_colour);
        if (fields.dealer) filterValues.dealers.add(fields.dealer);
      });

      // Update the filter dropdowns
      this.updateFilterDropdown('year', Array.from(filterValues.years).sort(), 'All Years');
      this.updateFilterDropdown('model', Array.from(filterValues.models).sort(), 'All Models');
      this.updateFilterDropdown('trim', Array.from(filterValues.trim_levels).sort(), 'All Trims');
      this.updateFilterDropdown('rtr_package', Array.from(filterValues.rtr_packages).sort(), 'All Packages');
      this.updateFilterDropdown('engine', Array.from(filterValues.engines).sort(), 'All Engines');
      this.updateFilterDropdown('colour', Array.from(filterValues.colours).sort(), 'All Colors');
      this.updateFilterDropdown('dealer', Array.from(filterValues.dealers).sort(), 'All Dealers');
    }

    updateFilterDropdown(name, values, defaultLabel) {
      const select = this.filterForm.querySelector(`select[name="${name}"]`);
      if (!select) return;

      // Store current selection
      const currentValue = select.value;

      // Clear and repopulate
      select.innerHTML = `<option value="">${defaultLabel}</option>`;
      
      values.forEach(value => {
        if (value && value.trim()) { // Only add non-empty values
          const option = document.createElement('option');
          option.value = value;
          option.textContent = value;
          if (value === currentValue) option.selected = true;
          select.appendChild(option);
        }
      });
    }
}
  
customElements.define('dealer-cars-filter', DealerCarsFilter);